import React, { useState, useEffect } from 'react';
import './HygieneRequestForm.css';

const ProductRequestForm = () => {
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Charger toutes les données
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        setDataLoading(true);
        
        const [locationsRes, categoriesRes, productsRes] = await Promise.all([
          fetch('/api/Locations/getAllLocations'),
          fetch('/api/categories'),
          fetch('/api/hygiene/products')
        ]);

        // Charger les locations
        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocations(Array.isArray(locationsData) ? locationsData : []);
        }

        // Charger les catégories
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData);
          // Sélectionner la première catégorie par défaut
          if (categoriesData.length > 0) {
            setSelectedCategory(categoriesData[0].name);
          }
        }

        // Charger les produits
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
        }

      } catch (error) {
        setError(`Erreur de chargement: ${error.message}`);
      } finally {
        setLoading(false);
        setDataLoading(false);
      }
    };

    loadAllData();
  }, []);

  // Filtrer les produits par catégorie sélectionnée
  const filteredProducts = selectedCategory 
    ? products.filter(product => product.category === selectedCategory)
    : [];

  const handleCheckboxChange = (productId, checked) => {
    if (checked) {
      setSelectedProducts(prev => ({ ...prev, [productId]: 1 }));
    } else {
      setSelectedProducts(prev => {
        const newSelection = { ...prev };
        delete newSelection[productId];
        return newSelection;
      });
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    if (quantity < 1) quantity = 1;
    setSelectedProducts(prev => ({ ...prev, [productId]: quantity }));
  };

  const incrementQuantity = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  };

  const decrementQuantity = (productId) => {
    setSelectedProducts(prev => {
      const currentQuantity = prev[productId] || 1;
      return {
        ...prev,
        [productId]: currentQuantity > 1 ? currentQuantity - 1 : 1
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      alert('Veuillez sélectionner une localisation');
      return;
    }

    if (Object.keys(selectedProducts).length === 0) {
      alert('Veuillez sélectionner au moins un produit');
      return;
    }

    setSubmitting(true);

    try {
      const results = [];
      const username = localStorage.getItem('username') || 'Utilisateur';
      
      for (const [productId, quantity] of Object.entries(selectedProducts)) {
        const product = products.find(p => p._id === productId);
        
        if (product && quantity > 0) {
          const requestData = {
            productId: productId,
            productName: product.name,
            quantity: quantity,
            unit: product.unit,
            requestedBy: username,
            location: selectedLocation,
            comments: comment,
            price: product.defaultPrice || 0
          };
          
          const response = await fetch('/api/hygiene/requests', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData),
          });
          
          if (response.ok) {
            const result = await response.json();
            results.push(result);
          } else {
            const errorData = await response.json();
            throw new Error(errorData.message || `Erreur pour ${product.name}`);
          }
        }
      }

      setSelectedProducts({});
      setSelectedLocation('');
      setComment('');
      alert(`✅ ${results.length} demande(s) soumise(s) avec succès!`);
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="hygiene-form-container">
      <div className="hygiene-loading">
        <div className="hygiene-spinner"></div>
        Chargement des données...
      </div>
    </div>
  );

  return (
    <div className="hygiene-form-container">
      <div className="hygiene-container">
        <div className="hygiene-card">
          <div className="hygiene-header">
            <h2>Demande de Produits</h2>
            <p>Sélectionnez les produits par catégorie</p>
          </div>
          
          {error && (
            <div className="hygiene-alert-error">
              {error}
            </div>
          )}

          {/* Badge de sélection */}
          {Object.keys(selectedProducts).length > 0 && (
            <div className="hygiene-selection-badge">
              {Object.keys(selectedProducts).length} produit(s) sélectionné(s)
            </div>
          )}

          <form onSubmit={handleSubmit} className="hygiene-form">
            {/* Sélection de la localisation */}
            <div className="hygiene-form-group">
              <label className="hygiene-label">
                Localisation *
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="hygiene-select"
                required
              >
                <option value="">Sélectionnez une localisation</option>
                {locations.map(location => (
                  <option key={location._id} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélection de la catégorie */}
            <div className="hygiene-form-group">
              <label className="hygiene-label">
                Catégorie de produits *
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="hygiene-select"
                required
              >
                <option value="">Sélectionnez une catégorie</option>
                {categories.map(category => (
                  <option key={category._id} value={category.name}>
                    {category.name} ({products.filter(p => p.category === category.name).length} produits)
                  </option>
                ))}
              </select>
            </div>

            {/* Section produits */}
            <div className="hygiene-products-section">
              <h3 className="hygiene-products-title">
                Produits {selectedCategory && `- ${selectedCategory}`}
                {dataLoading && ' (Chargement...)'}
                {!dataLoading && selectedCategory && ` (${filteredProducts.length} produits)`}
              </h3>
              
              {dataLoading ? (
                <div className="hygiene-loading-text">
                  <div className="hygiene-spinner"></div>
                  Chargement des produits...
                </div>
              ) : !selectedCategory ? (
                <div className="hygiene-alert-info">
                  <p>Veuillez sélectionner une catégorie pour afficher les produits</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="hygiene-alert-warning">
                  <p>Aucun produit trouvé dans cette catégorie</p>
                  <p>Veuillez sélectionner une autre catégorie</p>
                </div>
              ) : (
                <div className="hygiene-products-grid">
                  {filteredProducts.map((product) => (
                    <div 
                      key={product._id} 
                      className={`hygiene-product-card ${selectedProducts[product._id] ? 'hygiene-product-selected' : ''}`}
                    >
                      <div className="hygiene-product-header">
                        <input
                          type="checkbox"
                          checked={!!selectedProducts[product._id]}
                          onChange={(e) => handleCheckboxChange(product._id, e.target.checked)}
                          className="hygiene-checkbox"
                        />
                        <div className="hygiene-product-details">
                          <div className="hygiene-product-name">{product.name}</div>
                          <div className="hygiene-product-info">
                            <span className="hygiene-product-unit">{product.unit}</span>
                            {product.defaultPrice > 0 && (
                              <span className="hygiene-product-price">{product.defaultPrice}DT</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {selectedProducts[product._id] && (
                        <div className="hygiene-quantity-section">
                          <div className="hygiene-quantity-label">Quantité</div>
                          <div className="hygiene-quantity-controls">
                            <button
                              type="button"
                              className="hygiene-quantity-btn"
                              onClick={() => decrementQuantity(product._id)}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={selectedProducts[product._id] || 1}
                              onChange={(e) => handleQuantityChange(product._id, parseInt(e.target.value) || 1)}
                              className="hygiene-quantity-input"
                            />
                            <button
                              type="button"
                              className="hygiene-quantity-btn"
                              onClick={() => incrementQuantity(product._id)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section commentaire */}
            <div className="hygiene-form-group">
              <label className="hygiene-label">
                Commentaire *
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="hygiene-textarea"
                placeholder="Veuillez saisir un commentaire pour votre demande..."
                rows="3"
                required
              />
              {!comment && (
                <div className="hygiene-field-error">
                  Ce champ est obligatoire
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={Object.keys(selectedProducts).length === 0 || !comment || dataLoading || submitting}
              className="hygiene-submit-btn"
            >
              {submitting ? (
                <>
                  <div className="hygiene-spinner"></div>
                  Soumission en cours...
                </>
              ) : (
                `Soumettre la Demande (${Object.keys(selectedProducts).length} produits)`
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductRequestForm;